import { Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { ExceptionResolution } from '@usf/ngrx-order';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { UserCustomerInfo } from '../../../home/models/user-customer-info.model';
import { HomeService } from '../../../home/services/home.service';
import { ProductService } from '@shared/services/product/product.service';
import { EmittedResolution } from './models/emitted-resolution.model';
import { OrderExceptionViewModel } from './models/order-exception-view.model';
import { OrderExceptionService } from './service/order-exception.service';
import { ExceptionType } from './constants/order-exception-type.enum';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { ModalController, PopoverController } from '@ionic/angular';
import { HandPricingPopoverComponent } from '@shared/components/hand-pricing-popover/hand-pricing-popover.component';
import { SelectPricingService } from '@usf/ngrx-pricing';
import { ResolutionType } from './constants/resolution-type.enum';
import { EmittedPartialFlag } from './models/emitted-partial-flag-selection.model';
import { EmittedFlippedProductNumber } from './models/emitted-flipped-product-number.model';
import { WarningPopoverComponent } from '@shared/components/popovers/warning-popover/warning-popover.component';
import { PopoverHelperService } from '@shared/helpers/popover.helpers';
import { Moment } from 'moment';
import { EmittedSearchForSubs } from './models/emitted-search-for-subs.model';
import { HandPricingProductDetails } from '../../models/order-view-model';
import { ActivatedRoute } from '@angular/router';
import { ModalService } from '@shared/services/modal/modal.service';
import { DateExceptionCalendarComponent } from '../../components/order-exceptions/date-exception-calendar/date-exception-calendar.component';
import { FEATURES } from '@shared/constants/splitio-features';
import { mapHandPricingDetails } from '@product-detail/utils/cpr.util';
import { HandPricingCPRandNGPComponent } from '@shared/components/hand-pricing-cpr-ngp-modal/hand-pricing-cpr-ngp-modal.component';
import { SharedTrackingService } from '@shared/services/analytics/shared-tracking.service';
import {
  AnalyticsProduct,
  AnalyticsSubstituteProduct,
} from '@shared/models/tracking/analytics/analytics-product';
import { QuantityType } from '@shared/models/quantity-type';
import { OrderPunchoutService } from '@order/services/order-punchout.service';
import { OrderMinimumViewModel } from '@order/models/order-minimum-view-model';
import { OrderService } from '@order/services/order.service';
import { CPRModalComponent } from '@shared/components/cpr-modal/cpr-modal.component';
import { SimpleActionModalComponent } from '@shared/components/simple-action-modal/simple-action-modal.component';

@Component({
  selector: 'app-order-exceptions',
  templateUrl: './order-exceptions.page.html',
  styleUrls: ['./order-exceptions.page.scss'],
})
export class OrderExceptionsPage {
  vm: OrderExceptionViewModel;
  vmSub: Subscription;
  orderSub: Subscription;
  orderCustomerSub: Subscription;
  productNumberSub: Subscription;
  featureFlagsSub$: Subscription = new Subscription();
  userCustomer$: Observable<UserCustomerInfo>;
  pageError = false;
  orderId: string;
  dateChange: boolean;
  selectedDate: string;
  manageCustomizationsLogo: boolean;
  resolveLaterClick = false;
  orderMinVm: OrderMinimumViewModel;
  isCommonCPREnabled = false;
  isPartialExceptionsEnabled = false;

  constructor(
    readonly platformService: PlatformService,
    private orderExceptionService: OrderExceptionService,
    private productService: ProductService,
    private homeService: HomeService,
    private translate: TranslateService,
    private navigationHelperService: NavigationHelperService,
    public modalController: ModalController,
    private selectSellerPricingService: SelectPricingService,
    private popoverHelperService: PopoverHelperService,
    public popoverController: PopoverController,
    private route: ActivatedRoute,
    private panAppState: PanAppState,
    private modalService: ModalService,
    private orderPunchoutService: OrderPunchoutService,
    private orderService: OrderService,
  ) {}

  async ionViewWillEnter() {
    this.featureFlagsSub$ = combineLatest([
      this.panAppState.feature$([
        FEATURES.split_global_user_customizations.name,
      ]),
      this.panAppState.feature$([
        FEATURES.split_global_cpr_override_common_component.name,
      ]),
      this.panAppState.feature$([
        FEATURES.split_dynamic_order_partial_exceptions.name,
      ]),
    ]).subscribe(
      ([customizationsOn, isCommonCPREnabled, isPartialExceptionsEnabled]) => {
        this.manageCustomizationsLogo = customizationsOn;
        this.isCommonCPREnabled = isCommonCPREnabled;
        this.isPartialExceptionsEnabled = isPartialExceptionsEnabled;
      },
    );
    this.orderId = this.route.snapshot.paramMap.get('orderId');

    const orderExceptionInitialized =
      await this.orderExceptionService.isOrderExceptionStateInitialized(
        this.orderId,
      );
    if (!orderExceptionInitialized) {
      this.orderExceptionService.setExceptionsForOrderId(this.orderId);
    }

    let priceCallMade = false;
    this.userCustomer$ = this.homeService.loadUserCustomer$();

    this.orderCustomerSub = combineLatest([
      this.orderExceptionService.getOrderWithId(this.orderId),
      this.homeService.loadSelectedCustomerState$(),
    ])
      .pipe(
        filter(
          ([order, userCustomer]) =>
            order?.orderHeader?.customerNumber === userCustomer?.customerNumber,
        ),
        take(1),
      )
      .subscribe(([order, customer]) => {
        if (
          order?.orderHeader?.orderExceptions?.length < 1 &&
          !this.pageError
        ) {
          this.pageError = true;
          this.orderExceptionService.handleOrderHasNoExceptions(this.orderId);
        }
        this.productNumberSub = this.orderExceptionService
          .getOrderExceptionProductNumbers()
          .pipe(
            filter(
              productNumbers => !!productNumbers && productNumbers.length > 0,
            ),
          )
          .subscribe(productNumbers => {
            this.productService.loadProducts(productNumbers);
          });

        this.vmSub = this.orderExceptionService
          .getOrderExceptionViewModel()
          .pipe(
            tap(viewModel => {
              if (
                viewModel.orderExceptions?.has(ExceptionType.PRICE) &&
                viewModel.productMap
              ) {
                const priceProductNumbers =
                  OrderExceptionService.getProductNumberForExceptionType(
                    ExceptionType.PRICE,
                    viewModel.orderExceptions,
                  );
                if (!!priceProductNumbers.length && !priceCallMade) {
                  priceCallMade = true;
                  this.selectSellerPricingService.getSellerPrices(
                    priceProductNumbers,
                  );
                }
              }
            }),
          )
          .subscribe(vm => {
            this.vm = vm;
          });

        this.orderExceptionService // expand first exception on first load
          .getOrderExceptionViewModel()
          .pipe(
            filter(vm => !!vm.orderExceptions?.size), // check if the orderExceptions is not empty
            take(1), // only on first load
          )
          .subscribe(vm => {
            this.orderExceptionService.updateExpanded(
              true,
              (Array.from(this.vm.orderExceptions.keys()) || []) // get the list of keys as an Array
                .sort()[0], // sort the order exception and pick the first one to expand it
            );
          });
      });

    combineLatest([
      this.orderExceptionService.getOrderExceptionViewModel(),
      this.orderExceptionService.getOrderExceptionProductNumbers(),
      this.orderService.loadOrderMinimumViewModel$(this.vm?.order?.orderHeader),
    ])
      .pipe(
        filter(([vm, pn, orderMinVm]) =>
          pn.every(n => !!vm?.productMap?.get(n)),
        ),
        filter(([vm, pn, orderMinVm]) => !!vm.orderExceptions?.size),
        take(1),
      )
      .subscribe(([vm, pn, orderMinVm]) => {
        let productTrackingData: Array<
          AnalyticsProduct | AnalyticsSubstituteProduct
        > = [];
        for (let [type, excResolution] of vm?.orderExceptions) {
          excResolution.forEach(exception => {
            if (exception?.orderItem) {
              const prod: AnalyticsProduct = {
                divisionApn: `${vm?.order?.orderHeader?.divisionNumber}-${exception.orderItem?.productNumber}`,
                quantity:
                  exception.orderItem.eachesOrdered?.currentValue > 0
                    ? exception.orderItem.eachesOrdered.currentValue
                    : exception.orderItem.unitsOrdered.currentValue,
                quantityUnits: exception.orderItem.eachesOrdered?.currentValue
                  ? QuantityType.Eaches
                  : QuantityType.Cases,
                attributes: vm?.productMap.get(
                  exception.orderItem?.productNumber,
                )?.trackingAttributes,
                exceptionView: '1',
                exceptionNumberOfSubs:
                  exception?.orderException?.subOrReplaceItems.length,
                exceptionCode: exception?.orderException?.errorCode,
              };
              productTrackingData.push(prod);

              exception?.orderException?.subOrReplaceItems.forEach(sub => {
                const subProd: AnalyticsSubstituteProduct = {
                  divisionApn: `${vm?.order?.orderHeader?.divisionNumber}-${sub.productNumber}`,
                  attributes: vm?.productMap.get(sub.productNumber)
                    ?.trackingAttributes,
                  substitutionView: '1',
                  substitutionParentApn: `${vm?.order?.orderHeader?.divisionNumber}-${exception?.orderException?.productNumber}`,
                };
                productTrackingData.push(subProd);
              });
            }
          });
        }
        this.orderMinVm = orderMinVm;
        this.orderExceptionService.trackPageLoad(
          vm?.order,
          productTrackingData,
        );
      });
  }

  ionViewDidLeave() {
    if (this.vmSub) {
      this.vmSub.unsubscribe();
    }

    if (this.orderSub) {
      this.orderSub.unsubscribe();
    }

    if (this.productNumberSub) {
      this.productNumberSub.unsubscribe();
    }

    if (this.orderCustomerSub) {
      this.orderCustomerSub.unsubscribe();
    }

    if (this.featureFlagsSub$) {
      this.featureFlagsSub$.unsubscribe();
    }
  }

  resolveExceptionHandler(resolution: EmittedResolution) {
    this.orderExceptionService.resolveException(
      resolution.exceptionResolution,
      resolution.resolutionType,
      resolution.subOrderItem,
    );
  }

  isExpandedChanged(event, exceptionType) {
    this.orderExceptionService.updateExpanded(event, exceptionType);
  }

  resolvepartialReserveFlag(data: EmittedPartialFlag) {
    this.orderExceptionService.resolvepartialReserveFlag(data);
  }

  flipSubstituteProduct(data: EmittedFlippedProductNumber) {
    this.orderExceptionService.flipSubstituteProduct(data);
  }

  editRowClicked(data: ExceptionResolution) {
    this.orderExceptionService.editRowClicked(data);
  }

  cancelEditRowHandler(data: any) {
    this.orderExceptionService.cancelEditRowClicked(data);
  }

  trackByIndex = (index: number, id: any) => index;

  submit(): void {
    //if user has not changed the delivery date, set requested to confirmed to accomodate "off-day delivery" orders
    if (!this.dateChange) {
      this.vm = {
        ...this.vm,
        order: {
          ...this.vm?.order,
          orderHeader: {
            ...this.vm?.order?.orderHeader,
            requestedDeliveryDate:
              this.vm?.order?.orderHeader?.confirmedDeliveryDate,
          },
        },
      };
    }

    const minOrderDollars =
      (this.orderMinVm?.previousRemainingDollars || 0) > 0
        ? parseFloat(this.orderMinVm?.previousRemainingDollars.toFixed(2))
        : 0;
    const minOrderCases =
      (this.orderMinVm?.previousRemainingCases || 0) > 0
        ? this.orderMinVm?.previousRemainingCases
        : 0;
    const minOrderDollarsDiff =
      (this.orderMinVm?.remainingDollars || 0) > 0
        ? parseFloat(this.orderMinVm?.remainingDollars.toFixed(2))
        : 0;
    const minOrderCasesDiff =
      (this.orderMinVm?.remainingCases || 0) > 0
        ? this.orderMinVm?.remainingCases
        : 0;

    const VmWithOrderMinimum = {
      ...this.vm,
      order: {
        ...this.vm?.order,
        orderHeader: {
          ...this.vm?.order?.orderHeader,
          minOrderDollars,
          minOrderCases,
          minOrderDollarsDiff,
          minOrderCasesDiff,
        },
      },
    };

    if (this.vm?.partnerAutotransfer) {
      this.orderPunchoutService.createSpinnerWithMessageModal();
    }
    this.orderExceptionService.resolveOrder(VmWithOrderMinimum);
  }

  async resolveLaterClicked() {
    const isTouchModal = !(
      this.platformService.platformType === PlatformEnum.desktop
    );
    this.modalService.setModalOptions(
      isTouchModal,
      SimpleActionModalComponent,
      {
        title: this.translate.instant('i18n.leavePageModal.title'),
        description: this.translate.instant('i18n.leavePageModal.resolveLater'),
        leftButtonText: this.translate.instant('i18n.common.cancel'),
        rightButtonText: this.translate.instant(
          'i18n.leavePageModal.leavePage',
        ),
        exitButtonForTablet: true,
        exitButtonForMobile: true,
      },
      'simple-action-modal',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    modal.onDidDismiss().then((resp: any) => {
      const confirmed = !!resp.data?.dispatchAction;
      if (confirmed) {
        this.resolveLaterClick = true;
        const orderId =
          `${this.vm?.order?.orderHeader?.customerNumber}:` +
          `${this.vm?.order?.orderHeader?.departmentNumber}:` +
          `${this.vm?.order?.orderHeader?.orderId}`;
        this.orderExceptionService.clearOrderExceptions();
        this.navigationHelperService.routeToSubmittedOrder(orderId);
      }
    });

    return await modal.present();
  }

  openSearchForSubs(emittedSearchForSubs: EmittedSearchForSubs) {
    const productNumber =
      emittedSearchForSubs.exceptionResolution.orderException.productNumber;
    this.orderExceptionService.navigateToSearchForSubs(
      this.vm.productMap.get(productNumber).summary,
      emittedSearchForSubs.exceptionResolution.orderException.errorCode +
        ':' +
        productNumber,
      this.orderId,
      emittedSearchForSubs.searchText,
    );
  }

  navigateToCompareProductsPage(exceptionResolution: ExceptionResolution) {
    this.orderExceptionService.navigateToCompareProductsPage(
      exceptionResolution,
      this.orderId,
    );
  }

  async presentHandPricingModal(productDetails: HandPricingProductDetails) {
    const sellerPriceDetail =
      this.vm?.sellerPrice[productDetails?.currentDetails?.productNumber];
    const altSellerPriceDetail =
      this.vm?.sellerPrice[productDetails?.altProductDetails?.productNumber];
    const sellerHandPricingDetail = mapHandPricingDetails(
      sellerPriceDetail,
      productDetails?.currentDetails,
      true,
    );
    const altHandPricingDetail = mapHandPricingDetails(
      altSellerPriceDetail,
      productDetails?.altProductDetails,
      true,
    );

    const product = this.vm?.productMap?.get(
      productDetails?.currentDetails?.productNumber,
    );
    const alternativeProduct = this.vm?.productMap?.get(
      productDetails?.altProductDetails?.productNumber,
    );

    const productTracingData = SharedTrackingService.tracingProductData(
      this.vm?.order?.orderItems,
      this.vm?.productMap,
    );
    const districtNgpCprInd = sellerPriceDetail?.districtNgpCprInd;

    const isCprModal = districtNgpCprInd === 'Y';

    const priceUom = sellerHandPricingDetail?.priceUom;

    if (this.vm && productDetails) {
      if (isCprModal) {
        if (this.isCommonCPREnabled) {
          this.modalService.setModalOptions(
            !!this.platformService.isTouch.value,
            CPRModalComponent,
            {
              product,
              sellerPriceDetail,
              alternativeProduct,
              alternativeSellerPriceDetail: altSellerPriceDetail,
            },
            'modal-desktop-size-xl',
          );
        } else {
          this.modalService.setModalOptions(
            !!this.platformService.isTouch.value,
            HandPricingCPRandNGPComponent,
            {
              sellerPriceDetail,
              altSellerPriceDetail,
              sellerHandPricingDetail,
              altHandPricingDetail,
              isCprSetupInd: false,
              districtNgpCprInd,
              priceUom,
              orderHeader: this.vm.order?.orderHeader,
              productTracing: productTracingData,
            },
            'modal-desktop-size-xl',
          );
        }
      } else {
        const currentDetails = productDetails.currentDetails;
        currentDetails.currentPrice = this.getCurrentPrice(productDetails);
        this.modalService.setModalOptions(
          this.platformService.isTouch.value,

          HandPricingPopoverComponent,
          {
            productDetails: currentDetails,
            altProductDetails: productDetails.altProductDetails,
            sellerPrice: this.vm.sellerPrice[currentDetails.productNumber],
            altSellerPrice: null,
            currentPriceOverride: currentDetails.currentPriceOverride,
            errorMessage: currentDetails.orderException.errorMessage,
          },
          'modal-desktop-size-lg',
        );
      }

      const modal = await this.modalController.create(
        this.modalService.modalOptions,
      );

      modal.onDidDismiss().then(res => {
        if (!!res.data) {
          const exceptionResolution: ExceptionResolution = {
            orderException: productDetails.currentDetails.orderException,
            resolutionOrderItem: {
              originalProduct: {
                ...productDetails.currentDetails.orderItem,
                unitPrice: res.data.unitPrice || 0,
                unitPriceOverride: res.data.unitPriceOverride || 0,
              },
              substituteProduct: null,
            },
            expanded: false,
            updateTimeStamp: undefined,
          };
          this.orderExceptionService.resolveException(
            exceptionResolution,
            ResolutionType.PRICE,
            null,
          );
        }
      });

      return await modal.present();
    }
  }

  getCurrentPrice = productDetails => {
    return (
      productDetails?.exceptionResolution?.resolutionOrderItem?.originalProduct
        ?.unitPrice || productDetails.currentDetails.currentPrice
    );
  };

  async updateDateHandler(resolution: EmittedResolution) {
    // Determine which date to show as selected in the calendar
    const deliveryDate = OrderExceptionService.determineDeliveryDate(
      this.vm?.order?.orderHeader?.requestedDeliveryDate ||
        this.vm?.order?.orderHeader?.confirmedDeliveryDate,
      this.vm.nextDeliveryDate?.deliveryDate,
      resolution.exceptionResolution.resolutionDate,
    );
    if (!this.vm.online) {
      this.popoverHelperService.presentPopover(
        this.popoverController,
        WarningPopoverComponent,
        this.translate.instant('i18n.orderPage.offlineDeliveryDateErrorMsg'),
      );
    } else {
      this.modalService.setModalOptions(
        this.platformService.isTouch.value,
        DateExceptionCalendarComponent,
        {
          startDate: deliveryDate,
          eligibleDays: this.vm.deliveryDays,
          nextDeliveryDate: this.vm.nextDeliveryDate,
          chosenDate: resolution.exceptionResolution.resolutionDate,
        },
        'modal-calendar-desktop-size-xl',
      );

      const modal = await this.modalController.create(
        this.modalService.modalOptions,
      );

      modal.onDidDismiss().then((res: any) => {
        this.dateModalDismissHandler(res, resolution);
      });
      return await modal.present();
    }
  }

  private dateModalDismissHandler(res: any, resolution: EmittedResolution) {
    if (res?.data?.selectedDate) {
      const exceptionResolution: ExceptionResolution = {
        orderException: resolution.exceptionResolution.orderException,
        resolutionOrderItem: {
          originalProduct: null,
          substituteProduct: null,
        },
        expanded: true,
        resolutionDate: (res.data.selectedDate as Moment).toISOString(false),
        updateTimeStamp: new Date().toString(),
      };
      const previousRequestedDate = new Date(
        this?.vm?.order?.orderHeader?.requestedDeliveryDate,
      ).toISOString();
      const newDate = new Date(
        exceptionResolution.resolutionDate,
      ).toISOString();
      this.selectedDate = newDate;
      this.dateChange = previousRequestedDate !== newDate;
      this.orderExceptionService.resolveException(
        exceptionResolution,
        ResolutionType.DATE,
        null,
      );
    }
  }
}
